import React, { Fragment, useState, useMemo } from 'react';
import Link from 'shared/SlashLink';
import usePageSeo from '../../hooks/usePageSeo';
import JsonLd, { webAppSchema } from 'shared/JsonLd';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import ToolLeadCapture from '../../components/ToolLeadCapture/ToolLeadCapture';
// Rates + math live in the shared model (also used by the staff admin proposal
// builder) so public estimates and staff suggestions can never drift apart.
import { SCOPES, BUILDING_TYPES, computeBudget } from '../../data/lowVoltageRates';

// Brand tokens (this page has no existing local design system to extend).
const LVB_BLUE = '#0044EB';
const LVB_LINE = '#DCE5F2';
const LVB_SLATE = '#54648A';
const LVB_INK = '#16203C';

const lvb__css = `
.lvb__seg{display:inline-flex;flex-wrap:wrap;background:#F1F5FB;border:1.5px solid ${LVB_LINE};border-radius:13px;padding:4px;gap:3px}
.lvb__segbtn{border:none;background:transparent;border-radius:9px;padding:9px 16px;font-family:'Axiforma SemiBold',sans-serif;font-size:13.5px;color:${LVB_SLATE};cursor:pointer;transition:.15s;white-space:nowrap}
.lvb__segbtn:hover:not([aria-pressed=true]){color:${LVB_INK}}
.lvb__segbtn[aria-pressed=true]{background:#fff;color:${LVB_BLUE};box-shadow:0 3px 10px rgba(6,16,54,.12)}
@media(max-width:480px){.lvb__seg{display:flex;width:100%}.lvb__segbtn{flex:1;text-align:center}}
`;

// Joined toggle bar for the (3-option) building-type single-select.
const BuildingTypeSegmented = ({ value, onChange }) => (
    <div className="lvb__seg" role="group" aria-label="Building type">
        {BUILDING_TYPES.map((t) => (
            <button
                type="button"
                key={t.v}
                className="lvb__segbtn"
                aria-pressed={value === t.v}
                onClick={() => onChange(t.v)}
            >
                {t.label}
            </button>
        ))}
    </div>
);

const clampInt = (val, min, max) => {
    const n = parseInt(val, 10);
    if (Number.isNaN(n)) return min;
    return Math.min(max, Math.max(min, n));
};

const fmt = (n) => '$' + Math.round(n).toLocaleString('en-US');

const LowVoltageBudgetPage = () => {
    usePageSeo({
        title: 'Low-Voltage Budget Calculator — Cost per Unit',
        description: 'Free low-voltage budget calculator for multifamily and affordable housing — structured cabling, WiFi, surveillance, and access control cost per unit.',
        keywords: 'low voltage budget estimator, structured cabling cost per unit, multifamily low voltage, affordable housing technology budget, surveillance access control cost, fire life safety budget, BUILDLAB',
    });

    const [units, setUnits] = useState(80);
    const [type, setType] = useState('midrise');
    const [senior, setSenior] = useState(false);
    const [picked, setPicked] = useState(() => ({ cabling: true, wifi: true, surveillance: true, access: true, fls: false }));

    const toggle = (id) => setPicked((p) => ({ ...p, [id]: !p[id] }));

    const { perUnitLow, perUnitHigh, totalLow, totalHigh, anyScope } = useMemo(
        () => computeBudget({ units, type, senior, picked }),
        [units, type, senior, picked]
    );

    return (
        <Fragment>
            <style dangerouslySetInnerHTML={{ __html: lvb__css }} />
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={'Low-Voltage Budget Calculator'} pagesub={'Cost per Unit'} pageTop={'Construction Technology'} />
                <JsonLd data={webAppSchema({ name: 'Low-Voltage Budget Calculator', description: 'Free low-voltage budget calculator for multifamily and affordable housing — structured cabling, WiFi, surveillance, and access control cost per unit.', path: '/low-voltage-budget-estimator' })} />
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="row mb-4">
                            <div className="col-lg-9">
                                <h2 className="mb-3">Ballpark your low-voltage budget in seconds</h2>
                                <p>
                                    Planning a multifamily or affordable-housing build? Pick the systems you need and we'll estimate a
                                    budgeting range — structured cabling, WiFi, surveillance, door access, and fire &amp; life safety —
                                    scaled by unit count and building type. Use it for early pro-formas; a site walk produces the firm number.
                                </p>
                            </div>
                        </div>

                        <div className="row">
                            {/* Inputs */}
                            <div className="col-lg-7 mb-4">
                                <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 16, padding: '24px 28px' }}>
                                    <div className="row">
                                        <div className="col-sm-6 mb-3">
                                            <label className="input_title d-block mb-1">Number of units</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                min="1"
                                                max="2000"
                                                value={units}
                                                onChange={(e) => setUnits(clampInt(e.target.value, 1, 2000))}
                                                aria-label="Number of units"
                                            />
                                        </div>
                                        <div className="col-sm-6 mb-3">
                                            <label className="input_title d-block mb-1">Building type</label>
                                            <BuildingTypeSegmented value={type} onChange={setType} />
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="input_title d-block mb-2">Systems in scope</label>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            {SCOPES.map((s) => (
                                                <label key={s.id} style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer', fontSize: '.92rem' }}>
                                                    <input type="checkbox" checked={!!picked[s.id]} onChange={() => toggle(s.id)} />
                                                    {s.label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    <label style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer', fontSize: '.92rem' }}>
                                        <input type="checkbox" checked={senior} onChange={() => setSenior((v) => !v)} />
                                        Senior / special-needs housing (adds emergency-call complexity)
                                    </label>
                                </div>
                            </div>

                            {/* Estimate */}
                            <div className="col-lg-5 mb-4">
                                <div style={{ background: 'var(--bs-dark)', borderRadius: 16, padding: '28px 30px', color: '#fff', height: '100%' }}>
                                    <div style={{ fontSize: '.85rem', opacity: 0.8 }}>Estimated project budget</div>
                                    {anyScope ? (
                                        <>
                                            <div style={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1.1, margin: '4px 0 2px', color: '#7CFFB2' }}>
                                                {fmt(totalLow)} – {fmt(totalHigh)}
                                            </div>
                                            <div style={{ fontSize: '.9rem', opacity: 0.85, marginBottom: 14 }}>
                                                {fmt(perUnitLow)} – {fmt(perUnitHigh)} per unit · {units} units
                                            </div>
                                            <p style={{ fontSize: '.84rem', opacity: 0.82 }}>
                                                Range reflects building type, unit count efficiencies, and the systems you selected.
                                                Site conditions, finishes, redundancy, and AHJ requirements move the final number.
                                            </p>
                                        </>
                                    ) : (
                                        <p style={{ fontSize: '.92rem', opacity: 0.85, marginTop: 8 }}>Select at least one system to see an estimate.</p>
                                    )}
                                    <Link to="/contact" className="btn btn-primary w-100 mt-3" style={{ display: 'block', textAlign: 'center' }}>
                                        <span className="btn_label" data-text="Request a detailed bid">Request a detailed bid</span>
                                    </Link>
                                    <ToolLeadCapture
                                        tool="Low-Voltage Budget Estimator"
                                        summary={anyScope ? `${units} units (${type}) — est. budget ${fmt(totalLow)}–${fmt(totalHigh)} (${fmt(perUnitLow)}–${fmt(perUnitHigh)}/unit)` : `${units} units (${type}) — no systems selected yet`}
                                    />
                                </div>
                            </div>
                        </div>

                        <p className="small text-muted mt-3" style={{ maxWidth: 880 }}>
                            Estimates are planning-level ranges based on BUILDLAB's installed cost-per-unit experience on California
                            multifamily and affordable-housing projects. They are not a quote. Actual pricing depends on drawings,
                            site conditions, product selection, prevailing-wage requirements, and the authority having jurisdiction.
                            BUILDLAB designs and installs all of these systems coordinated across every trade.
                        </p>

                        <p className="small mt-3" style={{ maxWidth: 880 }}>
                            Related tools:{' '}
                            <Link to="/das-erccs-checker">DAS / ERRCS requirement checker</Link> ·{' '}
                            <Link to="/area-of-refuge-checker">Area of refuge requirements checker</Link> ·{' '}
                            <Link to="/tcac-broadband-estimator">TCAC broadband points estimator</Link> ·{' '}
                            <Link to="/low-voltage-project-request">Request a low-voltage design &amp; install proposal</Link>
                        </p>
                    </div>
                </section>
                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default LowVoltageBudgetPage;
